import { useState } from 'react';
import { useForm, useController, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '../../ui/core';
import { useToast } from '../../hooks';
import api from '../../lib/api';
import PersonSearchField from './PersonSearchField';

const REASON_OPTIONS = [
  { value: 'RIDER_REQUEST', label: 'Rider request' },
  { value: 'COMPANY_DECISION', label: 'Company decision' },
];

const NOTE_MAX = 500;

// 24-char Mongo hex id — the same shape the backend validates (bad id → 400).
// Validated client-side too so an obviously-malformed id is caught inline
// before a round-trip.
const MONGO_ID = /^[a-f0-9]{24}$/i;

const defaultValues = {
  riderMongoId: '',
  driverMongoId: '',
  reason: '',
  note: '',
};

export const schema = yup.object().shape({
  riderMongoId: yup
    .string()
    .required('Select a rider')
    .matches(MONGO_ID, 'Rider Mongo id must be a 24-character hex id'),
  driverMongoId: yup
    .string()
    .required('Select a driver')
    .matches(MONGO_ID, 'Driver Mongo id must be a 24-character hex id')
    // Reject a self-pair inline before the API is ever called — the server
    // rejects rider===driver with a 400, but there is no reason to make the
    // round-trip for something the form already knows is invalid.
    .test('not-self-pair', 'Rider and driver must be different users.', function (value) {
      const { riderMongoId } = this.parent;
      if (!value || !riderMongoId) return true;
      return value.toLowerCase() !== riderMongoId.toLowerCase();
    }),
  reason: yup
    .string()
    .required('Reason is required')
    // Same message for the whitelist check: yup evaluates oneOf before required
    // for an empty value, so without this an unselected reason would surface the
    // verbose "must be one of…" default instead of "Reason is required".
    .oneOf(['RIDER_REQUEST', 'COMPANY_DECISION'], 'Reason is required'),
  note: yup.string().max(NOTE_MAX, `Note must be ${NOTE_MAX} characters or fewer`),
});

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '10px',
    }}
  >
    {children}
  </Typography>
);

const ReasonField = ({ control }) => {
  const { field, fieldState } = useController({ name: 'reason', control });

  return (
    <FormControl fullWidth error={!!fieldState.error}>
      {/* No MUI InputLabel/notch — the uppercase FieldLabel above already names
          the field, matching the Rider/Driver/Note inputs. displayEmpty +
          renderValue supplies the placeholder the floating label used to. */}
      <Select
        displayEmpty
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        inputProps={{ 'aria-label': 'Reason' }}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Select a reason
              </Box>
            );
          }
          const option = REASON_OPTIONS.find((o) => o.value === selected);
          return option ? option.label : selected;
        }}
      >
        {REASON_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{fieldState.error ? fieldState.error.message : ' '}</FormHelperText>
    </FormControl>
  );
};

const NoteField = ({ control }) => {
  const { field, fieldState } = useController({ name: 'note', control });

  return (
    <TextField
      fullWidth
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      inputProps={{ 'aria-label': 'Note', maxLength: NOTE_MAX }}
      error={!!fieldState.error}
      helperText={fieldState.error ? fieldState.error.message : 'Optional'}
    />
  );
};

const CreateExclusionPanel = ({ onCreated }) => {
  const [serverError, setServerError] = useState(null);
  const toast = useToast();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, isSubmitting } = useFormState({ control: form.control });

  const onValid = async (values) => {
    setServerError(null);
    try {
      await api.riderDriverExclusions.createExclusion({
        riderMongoId: values.riderMongoId.trim(),
        driverMongoId: values.driverMongoId.trim(),
        reason: values.reason,
        note: values.note ? values.note.trim() : undefined,
      });
      toast.success('Exclusion added.');
      // Clear the whole form — unlike the throttle panel (which keeps the city
      // for the next combo), each exclusion is an unrelated pair, so a blank
      // form is the right starting point for the next one.
      form.reset(defaultValues);
      onCreated && (await onCreated());
    } catch (error) {
      // 409 (duplicate active pair) / 400 (self-pair, bad id) — surface the
      // server's message inline, verbatim, rather than as a toast.
      setServerError(error.message);
    }
  };

  return (
    <Box component="form" onSubmit={form.handleSubmit(onValid)}>
      <Stack spacing="24px">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Rider</FieldLabel>
            <PersonSearchField
              control={form.control}
              name="riderMongoId"
              kind="user"
              placeholder="Search by name, phone, or email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Driver</FieldLabel>
            <PersonSearchField
              control={form.control}
              name="driverMongoId"
              kind="driver"
              placeholder="Search by name, phone, or email"
            />
          </Grid>
        </Grid>

        <Box>
          <FieldLabel>Reason</FieldLabel>
          <ReasonField control={form.control} />
        </Box>

        <Box>
          <FieldLabel>Note</FieldLabel>
          <NoteField control={form.control} />
        </Box>

        {serverError && <Alert severity="error">{serverError}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Adding…' : 'Add exclusion'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default CreateExclusionPanel;
